// Location: src/components/admin/AddProfileSection.tsx
// Purpose: Add profile section for adding members to a pod
// Scope: Shows profiles that match the pod's platform and status

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Button } from '@/components/ui/button';

import { Pod, PodMember } from '@/types/pods';
import {
	canonicalPodPlatform,
	getPodModeCapability,
	getPodPlatformIcon,
	getPodPlatformLabel,
} from '@/types/pods';
import { Profile } from '@/types/profiles';
import { Id } from '@/convex/_generated/dataModel';

interface AddProfileSectionProps {
	pod: Pod;
	typedProfiles?: Profile[];
	newMember: PodMember;
	setNewMember: (member: PodMember) => void;
	handleAddMember: (
		podId: Id<'engagementPods'>,
		member: PodMember
	) => Promise<void>;
	isAddingMember?: boolean;
}

export function AddProfileSection({
	pod,
	typedProfiles,
	newMember,
	setNewMember,
	handleAddMember,
	isAddingMember,
}: AddProfileSectionProps) {
	const podMode = pod.mode === 'broadcast' ? 'broadcast' : 'swarm';
	const modeCapability = getPodModeCapability(pod.platform, podMode);
	const platformLabel = getPodPlatformLabel(pod.platform);

	// Filter profiles to only show those that match the pod's platform
	const linkedProfiles = React.useMemo(() => {
		if (!modeCapability.enabled) return [];
		return (typedProfiles || []).filter((profile) => {
			const podPlatform = canonicalPodPlatform(pod.platform);

			// Check if profile has a matching platform in platforms array
			const matchingPlatform = profile.platforms?.find((p) => {
				return canonicalPodPlatform(p.platform) === podPlatform;
			});

			// Check linkStatuses array for matching platform
			const matchingStatus = profile.linkStatuses?.find((status) => {
				if (status.status !== 'linked') return false;
				return canonicalPodPlatform(status.platform) === podPlatform;
			});

			// Only include profile if it has either a matching platform or linked status
			return matchingPlatform || matchingStatus;
		});
	}, [typedProfiles, pod.platform, modeCapability.enabled]);

	// Create a memoized render function for each profile
	const renderProfile = React.useCallback(
		(profile: Profile) => {
			const podPlatform = canonicalPodPlatform(pod.platform);

			// Find the matching platform info
			const platformInfo = profile.platforms?.find((p) => {
				return canonicalPodPlatform(p.platform) === podPlatform;
			});

			// If no platform info, try to get username from linkStatuses
			const linkedStatus = !platformInfo
				? profile.linkStatuses?.find((status) => {
						if (status.status !== 'linked') return false;
						return (
							canonicalPodPlatform(status.platform) ===
							podPlatform
						);
					})
				: undefined;

			const username =
				platformInfo?.username ||
				linkedStatus?.details?.match(/@(\w+)/)?.[1];
			const displayName =
				platformInfo?.displayName ||
				profile.profileTitle ||
				profile.email;

			// Get correct icon based on platform
			const getPlatformIcon = () => {
				return getPodPlatformIcon(pod.platform);
			};

			return (
				<div className='flex flex-col py-1'>
					<div className='font-medium'>{displayName}</div>
					{username && (
						<div className='text-sm text-gray-500 flex items-center gap-2'>
							<span className='flex items-center gap-1'>
								{getPlatformIcon()}@{username}
							</span>
						</div>
					)}
				</div>
			);
		},
		[pod.platform]
	);

	if (!modeCapability.enabled) {
		return (
			<Card>
				<CardContent className='pt-6'>
					<div className='rounded-md border border-orange-300 bg-orange-50 p-4 text-sm text-orange-900 dark:border-orange-900/60 dark:bg-orange-950/40 dark:text-orange-200'>
						<div className='font-semibold'>
							{platformLabel} {podMode} profiles disabled
						</div>
						<div className='mt-1'>{modeCapability.reason}</div>
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardContent className='pt-6'>
				<form
					className='space-y-4'
					onSubmit={(e) => {
						e.preventDefault();
						handleAddMember(pod._id, newMember);
					}}
				>
					<div className='flex gap-4'>
						<div className='flex-1'>
							<SearchableSelect
								options={linkedProfiles.map((profile) => ({
									value: profile.profileKey,
									label: profile.email,
									data: profile,
									render: () => renderProfile(profile),
								}))}
								maintainFocus={true}
								value={newMember.uploadPostProfileKey || ''}
								onChange={(value, option) => {
									if (option) {
										const podPlatform =
											canonicalPodPlatform(pod.platform);

										// Find matching platform info
										const platformInfo = (
											option.data as Profile
										).platforms?.find((p) => {
											return (
												canonicalPodPlatform(
													p.platform
												) === podPlatform
											);
										});

										// If no platform info, try to get username from linkStatuses
										const linkedStatus = !platformInfo
											? (
													option.data as Profile
												).linkStatuses?.find(
													(status) => {
														if (
															status.status !==
															'linked'
														)
															return false;
														return (
															canonicalPodPlatform(
																status.platform
															) ===
															podPlatform
														);
													}
												)
											: undefined;

										// Try multiple ways to get username
										let username =
											platformInfo?.username ||
											platformInfo?.displayName ||
											'';
										
										// If no username from platform info, try to extract from linkStatus details
										if (!username && linkedStatus?.details) {
											// Try different patterns for extracting username
											const patterns = [
												/@(\w+)/,  // Handle-style @username
												/username:\s*(\w+)/i,  // username: format
												/profile:\s*(\w+)/i,  // profile: format
												/\/(\w+)$/,  // URL ending with username
											];
											
											for (const pattern of patterns) {
												const match = linkedStatus.details.match(pattern);
												if (match && match[1]) {
													username = match[1];
													break;
												}
											}
										}
										
										// Fallback to profile title or email prefix if still no username
										if (!username) {
											const profile = option.data as Profile;
											username = profile.profileTitle || 
												profile.email.split('@')[0] ||
												'unknown';
										}

										setNewMember({
											email: (option.data as Profile)
												.email,
											socialUsername: username,
											uploadPostProfileKey: value,
										});
									}
								}}
								placeholder={`Select a ${platformLabel} profile...`}
							/>
						</div>
						<div className='relative group'>
							<Button
								type='submit'
								size='lg'
								disabled={
									!newMember.email ||
									!newMember.socialUsername ||
									isAddingMember ||
									pod.members.some(
										(member) =>
											member.email === newMember.email ||
											member.uploadPostProfileKey ===
												newMember.uploadPostProfileKey
									)
								}
								className='bg-gradient-to-b from-emerald-400 to-teal-600 hover:from-emerald-500 hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 px-6'
							>
								<span className='inline-flex items-center gap-2'>
									<span className='text-xl'>➕</span>
									<span>Add Profile</span>
								</span>
							</Button>
							{(!newMember.email ||
								!newMember.socialUsername ||
								pod.members.some(
									(member) =>
										member.email === newMember.email ||
										member.uploadPostProfileKey ===
											newMember.uploadPostProfileKey
								)) && (
								<div className='absolute -top-12 left-1/2 -translate-x-1/2 bg-black text-white px-4 py-2 rounded-lg text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap'>
									{!newMember.email ||
									!newMember.socialUsername
										? `Select a ${platformLabel} profile from the dropdown`
										: 'This profile is already in the pod'}
								</div>
							)}
						</div>
					</div>
					{!linkedProfiles.length && (
						<p className='text-sm text-gray-500 text-center'>
							No available {platformLabel} profiles found. Create
							and link a profile first.
						</p>
					)}
				</form>
			</CardContent>
		</Card>
	);
}
